import { OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import {
  Pwerm2CalculationCostOfEquityCaseSummaryCaseDto,
  Pwerm2CalculationCostOfEquityCaseSummaryDebtInstrumentDto,
  Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto,
  Pwerm2CalculationCostOfEquityInstrumentCaseYearDto,
  Pwerm2CalculationCostOfEquityInstrumentCaseYearTrancheDto,
  Pwerm2CalculationCostOfEquityTotalAttributableEquityYearDto,
  Pwerm2CalculationResultsDto,
  Pwerm2CalculationWaterfallCaseDto,
  Pwerm2CalculationWaterfallCaseInstrumentDto,
  Pwerm2CalculationWaterfallCaseInstrumentTrancheDto,
  Pwerm2CalculationWaterfallCaseYearDto,
  Pwerm2CalculationWaterfallInstrumentDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentTrancheConditionDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentTransactionSummaryDto,
  Pwerm2CalculationWaterfallCaseYearMultipleDto,
  Pwerm2CalculationWaterfallCaseYearMultipleInstrumentDto,
  Pwerm2CalculationWaterfallCaseYearMultipleInstrumentTrancheDto,
  Pwerm2CalculationWaterfallInstrumentTrancheDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import {
  Pwerm2InputCalculationResultsDto,
  Pwerm2InputsHistoricEventsCalculationResultsDto,
  Pwerm2InputsHistoricYearCalculationResultsDto,
  Pwerm2InputYearCalculationResultsDto,
} from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';

// Waterfall selectors
export const getWaterfallCaseValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  valueSelector: (caseItem: Pwerm2CalculationWaterfallCaseDto) => number | null
) => valueSelector(calculationResults.waterfall.cases[caseId]);

export const getWaterfallCaseYearValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  valueSelector: (year: Pwerm2CalculationWaterfallCaseYearDto) => number | string | boolean | null
) => valueSelector(calculationResults.waterfall.cases[caseId].years[yearId]);

export const getWaterfallCaseYearInstrumentValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  valueSelector: (instrument: Pwerm2CalculationWaterfallCaseYearInstrumentDto) => number | null
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId]
    );
  }

  return null;
};

export type CashFlowFlags = {
  hasInfusions: boolean;
  hasRedemptions: boolean;
  hasDividends: boolean;
  hasCashPayInterest: boolean;
};

export const getCashFlowFlagsForInstrument = (
  instrumentId: string,
  results: Pwerm2CalculationResultsDto
): CashFlowFlags => {
  const flags = {
    hasInfusions: false,
    hasRedemptions: false,
    hasDividends: false,
    hasCashPayInterest: false,
  };

  const waterfallCases = results?.waterfall?.cases;
  if (!waterfallCases) {
    return flags;
  }

  for (const caseData of Object.values(waterfallCases)) {
    const years = caseData?.years;

    if (!years) {
      continue;
    }

    for (const year of Object.values(years)) {
      const instruments = year?.instruments;

      if (!instruments) {
        continue;
      }

      const cashFlowSummary = Object.values(instruments).find(
        (i) => i?.instrumentId === instrumentId
      )?.cashFlowSummary;

      if (!cashFlowSummary) {
        continue;
      }

      if (!flags.hasInfusions && cashFlowSummary.totalAdditions) {
        flags.hasInfusions = true;
      }

      if (!flags.hasRedemptions && cashFlowSummary.totalRedemptions) {
        flags.hasRedemptions = true;
      }

      if (!flags.hasDividends && cashFlowSummary.dividends) {
        flags.hasDividends = true;
      }

      if (!flags.hasCashPayInterest && cashFlowSummary.cashPayInterest) {
        flags.hasCashPayInterest = true;
      }

      if (
        flags.hasInfusions &&
        flags.hasRedemptions &&
        flags.hasDividends &&
        flags.hasCashPayInterest
      ) {
        return flags;
      }
    }
  }

  return flags;
};

export const getWaterfallCaseYearInstrumentTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  trancheId: string,
  valueSelector: (tranche: Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto) => number
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId].tranches[
        trancheId
      ]
    );
  }

  return null;
};

const getWaterfallCaseYearInstrumentTransactionSummaryBase = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  valueSelector: (
    transactionSummary: Pwerm2CalculationWaterfallCaseYearInstrumentTransactionSummaryDto
  ) => number | null,
  hasValue: (
    transactionSummary: Pwerm2CalculationWaterfallCaseYearInstrumentTransactionSummaryDto
  ) => boolean,
  includeEmptyTransactionSummary: boolean
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    const transactionSummary =
      calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId]
        .transactionSummary;

    if (!transactionSummary) {
      return null;
    }

    if (hasValue(transactionSummary) || includeEmptyTransactionSummary) {
      return valueSelector(transactionSummary);
    }
  }

  return null;
};

export const getWaterfallCaseYearInstrumentInfusionTransactionSummary = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  valueSelector: (
    transactionSummary: Pwerm2CalculationWaterfallCaseYearInstrumentTransactionSummaryDto
  ) => number | null,
  includeEmptyTransactionSummary = false
) =>
  getWaterfallCaseYearInstrumentTransactionSummaryBase(
    calculationResults,
    caseId,
    yearId,
    instrumentId,
    valueSelector,
    (ts) => Boolean(ts.totalPvOfInfusionMovement),
    includeEmptyTransactionSummary
  );

export const getWaterfallCaseYearInstrumentTransactionSummary = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  valueSelector: (
    transactionSummary: Pwerm2CalculationWaterfallCaseYearInstrumentTransactionSummaryDto
  ) => number | null,
  includeEmptyTransactionSummary = false
) =>
  getWaterfallCaseYearInstrumentTransactionSummaryBase(
    calculationResults,
    caseId,
    yearId,
    instrumentId,
    valueSelector,
    (ts) =>
      Boolean(ts.totalCashPay) ||
      Boolean(ts.totalDividends) ||
      Boolean(ts.totalCashFlowMovementOfOtherTransactions),
    includeEmptyTransactionSummary
  );

export const getWaterfallCaseYearInstrumentTransaction = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    return calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId]
      .transactions;
  }

  return null;
};

export const getWaterfallCaseYearInstrumentTrancheConditionValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  trancheId: string,
  conditionIndex: number,
  valueSelector: (
    condition: Pwerm2CalculationWaterfallCaseYearInstrumentTrancheConditionDto
  ) => number
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    const conditions =
      calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId].tranches[
        trancheId
      ].conditions;

    const condition = conditions[conditionIndex];

    return valueSelector(condition!);
  }

  return null;
};

export const getYearTransactionsGroupedByCaseAndYear = (
  calculationResults: Pwerm2CalculationResultsDto,
  transactionsPropertySelector: (
    item: Pwerm2CalculationWaterfallCaseYearDto
  ) => Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]
): Record<
  string,
  Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
> => {
  const transactionsByCaseAndYear: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  > = {};

  Object.entries(calculationResults.waterfall.cases).forEach(([caseId, caseItem]) => {
    const transactionsByYear: Record<
      string,
      Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]
    > = {};

    transactionsByCaseAndYear[caseId] = transactionsByYear;

    Object.values(caseItem.years).forEach((year) => {
      const exitDate = year.exitDate;
      const transactions = Object.values(transactionsPropertySelector(year)) ?? [];

      if (!transactions.length) {
        return;
      }

      if (!transactionsByYear[exitDate]) {
        transactionsByYear[exitDate] = [];
      }

      transactionsByYear[exitDate].push(...transactions);
    });
  });

  return transactionsByCaseAndYear;
};

export const getTransactionsGroupedByCaseAndYear = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string | null,
  transactionsPropertySelector: (
    item: Pwerm2CalculationWaterfallCaseYearInstrumentDto
  ) => Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]
): Record<
  string,
  Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
> => {
  const transactionsByCaseAndYear: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  > = {};

  Object.entries(calculationResults.waterfall.cases).forEach(([caseId, caseItem]) => {
    const transactionsByYear: Record<
      string,
      Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]
    > = {};

    transactionsByCaseAndYear[caseId] = transactionsByYear;

    Object.values(caseItem.years).forEach((year) => {
      const exitDate = year.exitDate;
      const transactions =
        (instrumentId
          ? transactionsPropertySelector(year.instruments[instrumentId])
          : Object.values(year.instruments).flatMap((instrument) =>
              transactionsPropertySelector(instrument)
            )) ?? [];

      if (!transactions.length) {
        return;
      }

      if (!transactionsByYear[exitDate]) {
        transactionsByYear[exitDate] = [];
      }

      transactionsByYear[exitDate].push(...transactions);
    });
  });

  return transactionsByCaseAndYear;
};

export const getWaterfallCaseYearMultipleValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  multipleId: string,
  valueSelector: (multiple: Pwerm2CalculationWaterfallCaseYearMultipleDto) => number
) => valueSelector(calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId]);

export const getWaterfallCaseYearMultipleInstrumentValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  multipleId: string,
  instrumentId: string,
  valueSelector: (
    instrument: Pwerm2CalculationWaterfallCaseYearMultipleInstrumentDto
  ) => number | boolean
) => {
  if (
    instrumentId in
    calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments
  ) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments[
        instrumentId
      ]
    );
  }

  return null;
};

export const getWaterfallCaseYearMultipleInstrumentTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  multipleId: string,
  instrumentId: string,
  trancheId: string,
  valueSelector: (
    tranche: Pwerm2CalculationWaterfallCaseYearMultipleInstrumentTrancheDto
  ) => number | boolean
) => {
  if (
    instrumentId in
    calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments
  ) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments[
        instrumentId
      ].tranches[trancheId]
    );
  }

  return null;
};

export const getWaterfallCaseInstrumentValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  instrumentId: string,
  valueSelector: (instrument: Pwerm2CalculationWaterfallCaseInstrumentDto) => number
) => valueSelector(calculationResults.waterfall.cases[caseId].instruments[instrumentId]);

export const getWaterfallCaseInstrumentTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  instrumentId: string,
  trancheId: string,
  valueSelector: (tranche: Pwerm2CalculationWaterfallCaseInstrumentTrancheDto) => number
) =>
  valueSelector(
    calculationResults.waterfall.cases[caseId].instruments[instrumentId].tranches[trancheId]
  );

export const getWaterfallInstrumentValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  valueSelector: (instrument: Pwerm2CalculationWaterfallInstrumentDto) => number
) => valueSelector(calculationResults.waterfall.instruments[instrumentId]);

export const getWaterfallInstrumentTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  trancheId: string,
  valueSelector: (tranche: Pwerm2CalculationWaterfallInstrumentTrancheDto) => number
) => valueSelector(calculationResults.waterfall.instruments[instrumentId].tranches[trancheId]);

export const getWaterfallCaseYearInstrumentFirstTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  instrumentId: string,
  valueSelector: (tranche: Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto) => number
) => {
  if (instrumentId in calculationResults.waterfall.cases[caseId].years[yearId].instruments) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId].tranches[
        Object.keys(
          calculationResults.waterfall.cases[caseId].years[yearId].instruments[instrumentId]
            .tranches
        )[0]
      ]
    );
  }

  return null;
};

export const getWaterfallCaseYearMultipleInstrumentFirstTrancheValue = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  yearId: string,
  multipleId: string,
  instrumentId: string,
  valueSelector: (tranche: Pwerm2CalculationWaterfallCaseYearMultipleInstrumentTrancheDto) => number
) => {
  if (
    instrumentId in
    calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments
  ) {
    return valueSelector(
      calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId].instruments[
        instrumentId
      ].tranches[
        Object.keys(
          calculationResults.waterfall.cases[caseId].years[yearId].multiples[multipleId]
            .instruments[instrumentId].tranches
        )[0]
      ]
    );
  }

  return null;
};

// Cost of Equity selectors
export const getCaseSummariesCaseYear = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  forecastId: string,
  valueSelector: (year: Pwerm2CalculationCostOfEquityCaseSummaryCaseDto) => number
) => valueSelector(calculationResults.costOfEquity.caseSummaries[caseId].years[forecastId]);

export const getCaseSummariesCaseInitial = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  valueSelector: (initial: Pwerm2CalculationCostOfEquityCaseSummaryCaseDto) => number
) => valueSelector(calculationResults.costOfEquity.caseSummaries[caseId].initial);

export const getCaseSummariesCaseYearDebtInstrument = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  forecastId: string,
  debtInstrumentId: string,
  valueSelector: (instrument: Pwerm2CalculationCostOfEquityCaseSummaryDebtInstrumentDto) => number
) => {
  if (
    debtInstrumentId in
    calculationResults.costOfEquity.caseSummaries[caseId].years[forecastId].debtInstruments
  ) {
    return valueSelector(
      calculationResults.costOfEquity.caseSummaries[caseId].years[forecastId].debtInstruments[
        debtInstrumentId
      ]
    );
  }

  return null;
};

export const getCaseSummariesCaseInitialDebtInstrument = (
  calculationResults: Pwerm2CalculationResultsDto,
  caseId: string,
  debtInstrumentId: string,
  valueSelector: (instrument: Pwerm2CalculationCostOfEquityCaseSummaryDebtInstrumentDto) => number
) => {
  if (
    debtInstrumentId in
    calculationResults.costOfEquity.caseSummaries[caseId].initial.debtInstruments
  ) {
    return valueSelector(
      calculationResults.costOfEquity.caseSummaries[caseId].initial.debtInstruments[
        debtInstrumentId
      ]
    );
  }

  return null;
};

export const getInstrumentsCaseYear = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  forecastId: string,
  valueSelector: (year: Pwerm2CalculationCostOfEquityInstrumentCaseYearDto) => number
) =>
  valueSelector(
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].years[forecastId]
  );

export const getInstrumentsCaseInitial = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  valueSelector: (initial: Pwerm2CalculationCostOfEquityInstrumentCaseYearDto) => number
) => valueSelector(calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].initial);

export const getInstrumentCaseYearTranche = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  forecastId: string,
  trancheId: string,
  valueSelector: (
    tranche: Pwerm2CalculationCostOfEquityInstrumentCaseYearTrancheDto | null | undefined
  ) => number | null | undefined
) =>
  valueSelector(
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId]?.years[forecastId]
      .tranches[trancheId]
  );

export const getInstrumentCaseInitialTranche = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  trancheId: string,
  valueSelector: (
    tranche: Pwerm2CalculationCostOfEquityInstrumentCaseYearTrancheDto | null | undefined
  ) => number | null | undefined
) =>
  valueSelector(
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId]?.initial.tranches[
      trancheId
    ]
  );

export const getInstrumentCaseYearFirstTranche = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  forecastId: string,
  valueSelector: (tranche: Pwerm2CalculationCostOfEquityInstrumentCaseYearTrancheDto) => number
) => {
  const caseYear =
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].years[forecastId];
  if (caseYear) {
    const firstTranche = caseYear.tranches[Object.keys(caseYear.tranches)[0]];

    return valueSelector(firstTranche);
  }

  return null;
};

export const getInstrumentCaseInitialFirstTranche = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  valueSelector: (tranche: Pwerm2CalculationCostOfEquityInstrumentCaseYearTrancheDto) => number
) => {
  const caseYear = calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].initial;
  if (caseYear) {
    const firstTranche = caseYear.tranches[Object.keys(caseYear.tranches)[0]];

    return valueSelector(firstTranche);
  }

  return null;
};

export const getInstrumentCaseYearDebtInstrument = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  forecastId: string,
  debtInstrumentId: string,
  valueSelector: (
    debtInstrument: Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto
  ) => number
) => {
  const caseYear =
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].years[forecastId];
  if (caseYear) {
    if (debtInstrumentId in caseYear.debtInstruments) {
      return valueSelector(caseYear.debtInstruments[debtInstrumentId]);
    }

    return null;
  }

  return null;
};

export const getInstrumentCaseInitialDebtInstrument = (
  calculationResults: Pwerm2CalculationResultsDto,
  instrumentId: string,
  caseId: string,
  debtInstrumentId: string,
  valueSelector: (
    debtInstrument: Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto
  ) => number
) => {
  if (
    debtInstrumentId in
    calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].initial.debtInstruments
  ) {
    return valueSelector(
      calculationResults.costOfEquity.instruments[instrumentId].cases[caseId].initial
        .debtInstruments[debtInstrumentId]
    );
  }

  return null;
};

export const getTotalAttributableEquityYears = (
  calculationResults: Pwerm2CalculationResultsDto,
  forecastId: string,
  valueSelector: (years: Pwerm2CalculationCostOfEquityTotalAttributableEquityYearDto) => number
) =>
  valueSelector(
    calculationResults.costOfEquity.totalAttributableEquity.years.find(
      (year) => year.exitDate === forecastId
    )!
  );

// Deal Thesis Input selectors
export const getDealThesisInputCaseValuationDate = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  valueSelector: (caseItem: Pwerm2InputYearCalculationResultsDto) => number
) => valueSelector(calculationResults.cases[caseId].valuationDate);

export const getDealThesisInputCaseValuationDateShareholderDebtInstrumentsOwner = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  instrumentId: string,
  owner: OwnerType
) =>
  calculationResults.cases[caseId].valuationDate.shareholderDebtInstruments[instrumentId]
    .closingBalanceByOwner[owner];

export const getDealThesisInputCaseForecastDate = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  forecastDate: string,
  valueSelector: (caseItem: Pwerm2InputYearCalculationResultsDto) => number | null
) => valueSelector(calculationResults.cases[caseId].forecastDates[forecastDate]);

export const getDealThesisInputCaseHistoricForecastDate = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  historicForecastDate: string,
  valueSelector: (caseItem: Pwerm2InputsHistoricYearCalculationResultsDto) => number | null
) => {
  const caseData = calculationResults.cases?.[caseId];
  const historicDates = caseData?.historicDates;
  const historicYearData = historicDates?.historicForecastYears?.[historicForecastDate];

  if (historicYearData) {
    return valueSelector(historicYearData);
  }

  return null;
};

export const getDealThesisInputCaseHistoricEventsDate = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  historicEventsDate: string,
  valueSelector: (caseItem: Pwerm2InputsHistoricEventsCalculationResultsDto) => number | null
) => {
  const caseData = calculationResults.cases?.[caseId];
  const historicDates = caseData?.historicDates;
  const historicEventData = historicDates?.historicEventYears?.[historicEventsDate];

  if (historicEventData) {
    return valueSelector(historicEventData);
  }

  return null;
};

export const getDealThesisInputCaseForecastDateShareholderDebtInstrumentsOwner = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  forecastDate: string,
  instrumentId: string,
  owner: OwnerType
) =>
  calculationResults.cases[caseId].forecastDates[forecastDate]?.shareholderDebtInstruments[
    instrumentId
  ]?.closingBalanceByOwner[owner];

export const getDealThesisInputCaseValuationDateShareholderDebtTotalByType = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  instrumentType: string
) => calculationResults.cases[caseId].valuationDate.shareholderDebtTotalByType[instrumentType];

export const getDealThesisInputCaseForecastDateShareholderDebtTotalByType = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  forecastDate: string,
  instrumentType: string
) =>
  calculationResults.cases[caseId].forecastDates[forecastDate]?.shareholderDebtTotalByType[
    instrumentType
  ];

export const getDealThesisInputCaseHistoricEventsDateShareholderDebtTotalByType = (
  calculationResults: Pwerm2InputCalculationResultsDto,
  caseId: string,
  historicEventsDate: string,
  instrumentType: string
) => {
  const caseData = calculationResults.cases?.[caseId];
  const historicDates = caseData?.historicDates;
  const historicEventData = historicDates?.historicEventYears?.[historicEventsDate];
  const shareholderDebtTotalByType = historicEventData?.shareholderDebtTotalByType;

  if (shareholderDebtTotalByType) {
    return shareholderDebtTotalByType[instrumentType];
  }

  return null;
};

export const hasInterimTransactions = (results: Pwerm2CalculationResultsDto): boolean => {
  if (!results?.waterfall?.cases) {
    return false;
  }

  return Object.values(results.waterfall.cases).some((caseItem) =>
    Object.values(caseItem.years).some((year) => {
      return (
        year.infusionTransactions?.length > 0 ||
        year.redemptionTransactions?.length > 0 ||
        year.cashPaidTransactions?.length > 0 ||
        year.dividendTransactions?.length > 0
      );
    })
  );
};
